/**
 * Synchronous last-resort backup of not-yet-durable editor bodies.
 * IndexedDB drafts are primary; this exists so a sudden tab discard can still
 * recover text that never finished the async IDB write.
 */

export const LOCAL_UNSAVED_DRAFTS_KEY = 'sariz-notes:unsaved-drafts:v1';

export type LocalUnsavedDraft = {
  content: string;
  updatedAt: number;
};

type LocalUnsavedDraftMap = Record<string, LocalUnsavedDraft>;

/** Soft size budget under typical ~5MB localStorage limits. */
const MAX_SERIALIZED_BYTES = 2_500_000;

function isLocalUnsavedDraft(value: unknown): value is LocalUnsavedDraft {
  if (!value || typeof value !== 'object') {
    return false;
  }
  if (!('content' in value) || !('updatedAt' in value)) {
    return false;
  }
  return (
    typeof value.content === 'string' && typeof value.updatedAt === 'number'
  );
}

export function readLocalUnsavedDrafts(
  storage: Pick<Storage, 'getItem'> = localStorage,
): Map<string, LocalUnsavedDraft> {
  const result = new Map<string, LocalUnsavedDraft>();
  try {
    const raw = storage.getItem(LOCAL_UNSAVED_DRAFTS_KEY);
    if (!raw) {
      return result;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return result;
    }
    for (const [wsPath, value] of Object.entries(parsed)) {
      if (isLocalUnsavedDraft(value)) {
        result.set(wsPath, {
          content: value.content,
          updatedAt: value.updatedAt,
        });
      }
    }
  } catch {
    // Corrupt or unavailable storage must never break the editor.
  }
  return result;
}

/**
 * Writes the given drafts synchronously. Drops the largest entries first if
 * the serialized payload would exceed the soft budget.
 */
export function writeLocalUnsavedDrafts(
  drafts: ReadonlyMap<string, string>,
  storage: Pick<Storage, 'setItem' | 'removeItem'> = localStorage,
): void {
  try {
    if (drafts.size === 0) {
      storage.removeItem(LOCAL_UNSAVED_DRAFTS_KEY);
      return;
    }

    const updatedAt = Date.now();
    const entries = [...drafts.entries()].map(([wsPath, content]) => ({
      wsPath,
      content,
      bytes: wsPath.length + content.length,
    }));
    entries.sort((a, b) => a.bytes - b.bytes);

    const payload: LocalUnsavedDraftMap = {};
    let used = 2;
    for (const entry of entries) {
      const next = used + entry.bytes + 40;
      if (next > MAX_SERIALIZED_BYTES && Object.keys(payload).length > 0) {
        continue;
      }
      payload[entry.wsPath] = { content: entry.content, updatedAt };
      used = next;
    }

    storage.setItem(LOCAL_UNSAVED_DRAFTS_KEY, JSON.stringify(payload));
  } catch {
    try {
      storage.removeItem(LOCAL_UNSAVED_DRAFTS_KEY);
    } catch {
      // Ignore secondary failure.
    }
  }
}

export function clearLocalUnsavedDrafts(
  storage: Pick<Storage, 'removeItem'> = localStorage,
): void {
  try {
    storage.removeItem(LOCAL_UNSAVED_DRAFTS_KEY);
  } catch {
    // Ignore.
  }
}
